import React, { useState, useEffect } from 'react';
import { studentsAPI, groupsAPI, modulesAPI, degreesAPI, filtersAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';

const StudentsList = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [modules, setModules] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);
  
  // Helper functions for responsive table styling
  const getTableHeaderStyle = () => ({
    padding: isMobile ? '12px 8px' : '16px',
    textAlign: 'left',
    borderBottom: '1px solid #E5E7EB',
    fontSize: isMobile ? 12 : 14,
    fontFamily: 'Outfit',
    fontWeight: '600',
    color: '#374151',
    minWidth: isMobile ? '80px' : 'auto',
    whiteSpace: 'nowrap'
  });

  const getTableCellStyle = () => ({
    padding: isMobile ? '12px 8px' : '16px',
    borderBottom: '1px solid #E5E7EB',
    fontSize: isMobile ? 13 : 14,
    fontFamily: 'Outfit',
    color: '#111827',
    whiteSpace: isMobile ? 'nowrap' : 'normal'
  });
  
  // Filtered options for cascading dropdowns (similar to ReportsAnalytics)
  const [filteredDegrees, setFilteredDegrees] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  
  // Enhanced filter states (matching ReportsAnalytics structure)
  const [filters, setFilters] = useState({
    degreeLevel: '',
    academicStatus: '',
    academicYear: '',
    degree: '',
    year: '',
    semester: '',
    group: '',
    module: '',
    studentWithId: '',
    status: ''
  });

  // Fetch all data
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('=== FETCHING ALL DATA ===');
      
      const [studentsRes, groupsRes, modulesRes, degreesRes] = await Promise.all([
        studentsAPI.getAllStudents(),
        groupsAPI.getAllGroups(),
        modulesAPI.getAllModules(),
        degreesAPI.getAllDegrees()
      ]);

      console.log('Students response:', studentsRes.data);
      console.log('Groups response:', groupsRes.data);
      console.log('Modules response:', modulesRes.data);
      console.log('Degrees response:', degreesRes.data);
      
      const studentsData = studentsRes.data || [];
      const groupsData = groupsRes.data || [];
      const modulesData = modulesRes.data || [];
      const degreesData = degreesRes.data || [];
      
      setAllStudents(studentsData);
      setStudents(studentsData);
      setModules(modulesData);
      setDegrees(degreesData);
      
      // Initialize filtered data - show all initially, then filter based on selections
      setFilteredDegrees(degreesData);
      setFilteredGroups(groupsData); // Show all groups initially
      setFilteredModules(modulesData);
      setFilteredStudents([]);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Cascading filter effects (similar to ReportsAnalytics)
  
    // Effect for Degree Level and Academic Status changes - filters degrees
  useEffect(() => {
    const fetchFilteredDegrees = async () => {
      try {
        // If no relevant filters, show all degrees
        if (!filters.degreeLevel && !filters.academicStatus && !filters.academicYear) {
          console.log('🔍 Showing all degrees - no degree level/academic status/academic year filters');
          setFilteredDegrees(degrees);
          return;
        }

        console.log('🔍 Fetching filtered degrees with:', {
          degreeLevel: filters.degreeLevel,
          academicStatus: filters.academicStatus,
          academicYear: filters.academicYear
        });

        // Now pass all three parameters to the backend
        const response = await filtersAPI.getFilteredDegrees({
          degreeLevel: filters.degreeLevel,
          academicStatus: filters.academicStatus,
          academicYear: filters.academicYear
        });
        
        console.log('✅ Filtered degrees response:', response.data?.length || 0, 'degrees');
        if (response.data && response.data.length > 0) {
          console.log('Sample degrees:', response.data.slice(0, 3).map(d => ({
            degree_programme: d.degree_programme,
            degree_level: d.degree_level,
            local_international: d.local_international
          })));
        }
        setFilteredDegrees(response.data || []);
      } catch (error) {
        console.error('❌ Error fetching filtered degrees:', error);
        setFilteredDegrees([]);
      }
    };

    // Add small delay to prevent rapid fire requests
    const timeoutId = setTimeout(fetchFilteredDegrees, 50);
    return () => clearTimeout(timeoutId);
  }, [filters.degreeLevel, filters.academicStatus, filters.academicYear, degrees]);

  // Effect for Degree changes - filters groups
  useEffect(() => {
    const fetchFilteredGroups = async () => {
      try {
        // Only fetch groups if we have specific criteria: degree OR academic year
        if (!filters.degree && !filters.year && !filters.semester && !filters.academicYear) {
          console.log('🔍 Clearing groups - no specific criteria (need degree or academic year)');
          setFilteredGroups([]);
          return;
        }

        console.log('🔍 Fetching filtered groups with filters:', {
          degree: filters.degree,
          year: filters.year,
          semester: filters.semester,
          academicYear: filters.academicYear
        });
        
        const response = await filtersAPI.getFilteredGroups({
          degree: filters.degree,
          year: filters.year,
          semester: filters.semester,
          academicYear: filters.academicYear
        });
        
        console.log('✅ Filtered groups response:', response.data?.length || 0, 'groups');
        if (response.data && response.data.length > 0) {
          console.log('Sample groups:', response.data.slice(0, 3).map(g => ({
            group_name: g.group_name,
            year: g.year,
            semester: g.semester,
            degree_programme: g.degree_details?.degree_programme
          })));
        }
        setFilteredGroups(response.data || []);
      } catch (error) {
        console.error('❌ Error fetching filtered groups:', error);
        setFilteredGroups([]);
      }
    };

    // Add small delay to prevent race conditions
    const timeoutId = setTimeout(fetchFilteredGroups, 100);
    return () => clearTimeout(timeoutId);
  }, [filters.degree, filters.year, filters.semester, filters.academicYear]);

  // Effect for Group changes - filters modules
  useEffect(() => {
    const fetchFilteredModules = async () => {
      try {
        // Show all modules initially if no group/specific filters selected
        if (!filters.group && !filters.degree && !filters.year && !filters.semester) {
          console.log('🔍 Showing all modules - no specific group/course filters');
          setFilteredModules(modules);
          return;
        }
        
        // If we have group or course-specific filters, fetch filtered modules
        if (filters.group || (filters.degree && filters.year && filters.semester)) {
          console.log('🔍 Fetching filtered modules with criteria:', {
            degree: filters.degree,
            year: filters.year,
            semester: filters.semester,
            group: filters.group,
            academicYear: filters.academicYear
          });
          
          const response = await filtersAPI.getFilteredModules({
            degree: filters.degree,
            year: filters.year,
            semester: filters.semester,
            group: filters.group,
            academicYear: filters.academicYear
          });
          
          console.log('✅ Filtered modules response:', response.data?.length || 0, 'modules');
          setFilteredModules(response.data || []);
        } else {
          console.log('🔍 Clearing modules - insufficient criteria');
          setFilteredModules([]);
        }
      } catch (error) {
        console.error('❌ Error fetching filtered modules:', error);
        setFilteredModules([]);
      }
    };

    // Add small delay to ensure group filtering completes first
    const timeoutId = setTimeout(fetchFilteredModules, 150);
    return () => clearTimeout(timeoutId);
  }, [filters.degree, filters.year, filters.semester, filters.group, filters.academicYear, modules]);

  // Effect for Module changes - filters students
  useEffect(() => {
    const fetchFilteredStudents = async () => {
      try {
        // Only fetch students if we have meaningful filter criteria
        if (!filters.module && !filters.group && !filters.degree) {
          console.log('🔍 Clearing filtered students - no specific module/group/degree filters');
          setFilteredStudents([]);
          return;
        }
        
        console.log('🔍 Fetching filtered students with criteria:', {
          degreeLevel: filters.degreeLevel,
          academicStatus: filters.academicStatus,
          degree: filters.degree,
          year: filters.year,
          semester: filters.semester,
          group: filters.group,
          module: filters.module,
          academicYear: filters.academicYear
        });

        const response = await filtersAPI.getFilteredStudents({
          degreeLevel: filters.degreeLevel,
          academicStatus: filters.academicStatus,
          degree: filters.degree,
          year: filters.year,
          semester: filters.semester,
          group: filters.group,
          module: filters.module,
          academicYear: filters.academicYear
        });
        
        console.log('✅ Filtered students response:', response.data?.length || 0, 'students');
        setFilteredStudents(response.data || []);
      } catch (error) {
        console.error('❌ Error fetching filtered students:', error);
        setFilteredStudents([]);
      }
    };

    // Add longer delay to ensure previous filters complete
    const timeoutId = setTimeout(fetchFilteredStudents, 200);
    return () => clearTimeout(timeoutId);
  }, [filters.degreeLevel, filters.academicStatus, filters.degree, filters.year, filters.semester, filters.group, filters.module, filters.academicYear]);

  // Apply filters to students
  const applyFilters = () => {
    let filteredStudentsData = [...allStudents];

    // If we have cascaded filtered students and specific filters applied, use them
    if (filters.module && filteredStudents.length > 0) {
      // Use the students from the cascaded filter
      filteredStudentsData = filteredStudents;
    } else if (filters.group && filteredStudents.length > 0) {
      // Use the students from the group filter
      filteredStudentsData = filteredStudents;
    } else {
      // Apply manual filtering based on existing data
      
      // Apply degree level filter
      if (filters.degreeLevel) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.degree_level === filters.degreeLevel
        );
      }

      // Apply academic status filter
      if (filters.academicStatus) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.local_international === filters.academicStatus
        );
      }

      // Apply degree filter
      if (filters.degree) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.degree_programme === filters.degree
        );
      }

      // Apply group filter
      if (filters.group) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.group_name === filters.group
        );
      }

      // Apply year filter
      if (filters.year) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.current_year?.toString() === filters.year
        );
      }

      // Apply semester filter
      if (filters.semester) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.current_semester?.toString() === filters.semester
        );
      }

      // Apply specific student filter
      if (filters.studentWithId) {
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.student_id?.toLowerCase().includes(filters.studentWithId.toLowerCase()) ||
          student.name?.toLowerCase().includes(filters.studentWithId.toLowerCase())
        );
      }

      // Apply status filter (Active/Inactive)
      if (filters.status) {
        const isActiveFilter = filters.status === 'Active';
        filteredStudentsData = filteredStudentsData.filter(student => 
          student.active === isActiveFilter
        );
      }
    }

    console.log('Filtered students:', filteredStudentsData.length);
    setStudents(filteredStudentsData);
  };

  // Initial fetch
  useEffect(() => {
    fetchAllData();
  }, []);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle filter changes (similar to ReportsAnalytics)
  const handleFilterChange = (field, value) => {
    console.log(`🔄 Filter change: ${field} = "${value}"`);
    
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Reset dependent filters when parent filters change (but be more conservative)
      if (field === 'degreeLevel' || field === 'academicStatus') {
        // Only reset if changing to a different non-empty value
        if (value !== prev[field] && (value === '' || prev[field] === '')) {
          newFilters.degree = '';
          newFilters.year = '';
          newFilters.semester = '';
          newFilters.group = '';
          newFilters.module = '';
          newFilters.studentWithId = '';
          console.log('🔄 Reset all dependent filters due to degree level/academic status change');
        }
      } else if (field === 'academicYear') {
        // Academic year affects groups, modules, students
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
        console.log('🔄 Reset group/module/student filters due to academic year change');
      } else if (field === 'degree') {
        // Only reset if actually changing the degree
        if (value !== prev.degree) {
          newFilters.year = '';
          newFilters.semester = '';
          newFilters.group = '';
          newFilters.module = '';
          newFilters.studentWithId = '';
          console.log('🔄 Reset year/semester/group/module/student filters due to degree change');
        }
      } else if (field === 'year' || field === 'semester') {
        // Only reset if the value actually changed
        if (value !== prev[field]) {
          newFilters.group = '';
          newFilters.module = '';
          newFilters.studentWithId = '';
          console.log(`🔄 Reset group/module/student filters due to ${field} change`);
        }
      } else if (field === 'group') {
        // Only reset if group actually changed
        if (value !== prev.group) {
          newFilters.module = '';
          newFilters.studentWithId = '';
          console.log('🔄 Reset module/student filters due to group change');
        }
      } else if (field === 'module') {
        // Only reset if module actually changed
        if (value !== prev.module) {
          newFilters.studentWithId = '';
          console.log('🔄 Reset student filter due to module change');
        }
      } else if (field === 'status') {
        // Status filter is independent, doesn't reset other filters
        console.log('🔄 Status filter changed - no dependent resets');
      }
      
      console.log('🔄 New filters state:', newFilters);
      return newFilters;
    });
  };

  // Search function
  const handleSearch = () => {
    applyFilters();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      degreeLevel: '',
      academicStatus: '',
      academicYear: '',
      degree: '',
      year: '',
      semester: '',
      group: '',
      module: '',
      studentWithId: '',
      status: ''
    });
    setStudents(allStudents);
  };

  // Handle edit student
  const handleEditStudent = (studentId) => {
    console.log('Editing student with ID:', studentId);
    navigate(`/edit-student/${studentId}`);
  };

  // Download CSV function
  const downloadCSV = () => {
    console.log('=== Downloading Students CSV ===');
    console.log('Students to export:', students.length);
    
    if (!students || students.length === 0) {
      alert('No students data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'No.',
      'Student ID',
      'Name',
      'Email',
      'Degree Programme',
      'Degree Level',
      'Academic Status',
      'Group',
      'Current Year',
      'Current Semester',
      'Modules Enrolled',
      'Total Modules',
      'Status'
    ];

    // Convert students data to CSV rows
    const csvData = students.map((student, index) => {
      // Prepare modules information
      const modulesList = student.modules && student.modules.length > 0 
        ? student.modules.map(m => `${m.module_code} (${m.module_name})`).join('; ')
        : 'No modules enrolled';
      
      const modulesCount = student.modules ? student.modules.length : 0;

      return [
        (index + 1).toString().padStart(2, '0'),
        student.student_id || 'N/A',
        student.name || 'N/A',
        student.email || 'N/A',
        student.degree_programme || 'N/A',
        student.degree_level || 'N/A',
        student.local_international || 'N/A',
        student.group_name || 'N/A',
        student.current_year || 'N/A',
        student.current_semester || 'N/A',
        modulesList,
        modulesCount,
        student.active ? 'Active' : 'Inactive'
      ];
    });

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => 
        // Escape fields containing commas, quotes, or newlines
        typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
          ? `"${field.replace(/"/g, '""')}"` 
          : field
      ).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Generate descriptive filename with timestamp and filter info
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const activeFilters = [];
    
    if (filters.degreeLevel) activeFilters.push(`level-${filters.degreeLevel}`);
    if (filters.academicStatus) activeFilters.push(`status-${filters.academicStatus}`);
    if (filters.degree) activeFilters.push(`degree-${filters.degree.replace(/\s+/g, '-')}`);
    if (filters.group) activeFilters.push(`group-${filters.group}`);
    if (filters.year) activeFilters.push(`year-${filters.year}`);
    if (filters.semester) activeFilters.push(`sem-${filters.semester}`);
    if (filters.module) activeFilters.push(`module-${filters.module}`);
    if (filters.status) activeFilters.push(`student-status-${filters.status.toLowerCase()}`);
    if (filters.studentWithId) activeFilters.push(`search-${filters.studentWithId}`);
    
    const filterInfo = activeFilters.length > 0 
      ? `_filtered-by-${activeFilters.join('-')}` 
      : '_all-students';
    const filename = `students-list${filterInfo}_${timestamp}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`✅ CSV downloaded: ${filename}`);
    console.log(`📊 Exported ${students.length} students`);
    
    // Show success message
    const filterMessage = Object.values(filters).some(value => value !== '') 
      ? ` with applied filters` 
      : '';
    alert(`Successfully exported ${students.length} students${filterMessage} to CSV file: ${filename}`);
  };

  // Filter dropdown component (updated to handle new filter structure)
  const FilterDropdown = ({ label, name, options, value }) => (
    <div style={{position: 'relative', width: 164}}>
      <div style={{
        opacity: 0.65,
        color: '#444444',
        fontSize: 16,
        fontFamily: 'Outfit',
        marginBottom: 8
      }}>
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => handleFilterChange(name, e.target.value)}
        style={{
          width: '100%',
          height: 58,
          background: 'white',
          border: '1px solid #CED4DA',
          borderRadius: 6,
          padding: '0 15px',
          fontSize: 16,
          fontFamily: 'Outfit'
        }}
      >
        {options.map(option => (
          <option key={option} value={option}>
            {option === '' ? 'All' : option}
          </option>
        ))}
      </select>
    </div>
  );

  const formatDisplayValue = (value) => {
    if (!value) return 'N/A';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'white'
    }}>
      <Sidebar />
      
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : 290,
        padding: isMobile ? '80px 15px 20px 15px' : '0 40px 40px',
        overflow: 'auto'
      }}>
        <Header />
        
        {/* Page Title */}
        <div style={{ marginTop: 40 }}>
          <h1 style={{
            fontSize: 24,
            fontFamily: 'Outfit',
            fontWeight: '700',
            color: '#333',
            marginBottom: 8
          }}>
            Students Management
          </h1>
          <p style={{
            fontSize: 16,
            fontFamily: 'Outfit',
            color: '#666',
            margin: 0
          }}>
            Manage and view all students in your university
          </p>
        </div>

        {/* Filters Section */}
        <div style={{
          marginTop: 30,
          background: '#FFFFFF',
          borderRadius: 8,
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{
            fontSize: 18,
            fontFamily: 'Outfit',
            fontWeight: '600',
            marginBottom: 20,
            color: '#333'
          }}>
            Filter Students
          </h3>
          
          <div style={{
            display: 'flex',
            gap: 20,
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            {/* Filter dropdowns in the specified order */}
            
            {/* Degree Level */}
            <FilterDropdown 
              label="Degree Level" 
              name="degreeLevel" 
              options={['', 'undergraduate', 'postgraduate', 'doctoral']}
              value={filters.degreeLevel}
            />
            
            {/* Academic Status */}
            <FilterDropdown 
              label="Academic Status" 
              name="academicStatus" 
              options={['', 'local', 'international']}
              value={filters.academicStatus}
            />
            
            {/* Academic Year */}
            <FilterDropdown 
              label="Academic Year" 
              name="academicYear" 
              options={['']}
              value={filters.academicYear}
            />
            
            {/* Degree */}
            <FilterDropdown 
              label="Degree" 
              name="degree" 
              options={['', ...filteredDegrees.map(d => d.degree_programme)]}
              value={filters.degree}
            />
            
            {/* Year */}
            <FilterDropdown 
              label="Year" 
              name="year" 
              options={['', '1', '2', '3', '4']}
              value={filters.year}
            />
            
            {/* Semester */}
            <FilterDropdown 
              label="Semester" 
              name="semester" 
              options={['', '1', '2']}
              value={filters.semester}
            />
            
            {/* Group */}
            <FilterDropdown 
              label="Group" 
              name="group" 
              options={['', ...filteredGroups.map(g => g.group_name)]}
              value={filters.group}
            />
            
            {/* Module */}
            <FilterDropdown 
              label="Module" 
              name="module" 
              options={['', ...filteredModules.map(m => m.module_code)]}
              value={filters.module}
            />
            
            {/* Status */}
            <FilterDropdown 
              label="Status" 
              name="status" 
              options={['', 'Active', 'Inactive']}
              value={filters.status}
            />
            
            {/* Students with ID - Text Input */}
            <div style={{position: 'relative', width: 164}}>
              <div style={{
                opacity: 0.65,
                color: '#444444',
                fontSize: 16,
                fontFamily: 'Outfit',
                marginBottom: 8
              }}>
                Students with ID
              </div>
              <input
                type="text"
                placeholder="Search by ID or Name"
                value={filters.studentWithId}
                onChange={(e) => handleFilterChange('studentWithId', e.target.value)}
                style={{
                  width: '100%',
                  height: 58,
                  background: 'white',
                  border: '1px solid #CED4DA',
                  borderRadius: 6,
                  padding: '0 15px',
                  fontSize: 16,
                  fontFamily: 'Outfit'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                onClick={handleSearch}
                style={{
                  width: 120,
                  height: 52,
                  background: '#F04915',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#D63A0F'}
                onMouseOut={(e) => e.target.style.background = '#F04915'}
              >
                Search
              </button>
              
              <button 
                onClick={resetFilters}
                style={{
                  width: 120,
                  height: 52,
                  background: '#6B7280',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#4B5563'}
                onMouseOut={(e) => e.target.style.background = '#6B7280'}
              >
                Reset
              </button>

              <button 
                onClick={downloadCSV}
                disabled={!students || students.length === 0}
                style={{
                  width: 140,
                  height: 52,
                  background: students && students.length > 0 ? '#10B981' : '#D1D5DB',
                  borderRadius: 8,
                  border: 'none',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  fontWeight: '500',
                  cursor: students && students.length > 0 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
                onMouseOver={(e) => {
                  if (students && students.length > 0) {
                    e.target.style.background = '#059669';
                  }
                }}
                onMouseOut={(e) => {
                  if (students && students.length > 0) {
                    e.target.style.background = '#10B981';
                  }
                }}
                title={`Download ${students?.length || 0} students as CSV`}
              >
                📥 CSV Download
              </button>
            </div>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(filters.degreeLevel || filters.academicStatus || filters.academicYear || filters.degree || filters.year || filters.semester || filters.group || filters.module || filters.status || filters.studentWithId) && (
          <div style={{
            marginTop: 20,
            background: '#F3F4F6',
            borderRadius: 8,
            padding: '16px',
            border: '1px solid #E5E7EB'
          }}>
            <div style={{
              fontSize: 16,
              fontFamily: 'Outfit',
              fontWeight: '600',
              marginBottom: 12,
              color: '#374151'
            }}>
              Active Filters
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {filters.degreeLevel && (
                <span style={{
                  padding: '4px 12px',
                  background: '#E0F2FE',
                  color: '#0369A1',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Level: {filters.degreeLevel}
                </span>
              )}
              {filters.academicStatus && (
                <span style={{
                  padding: '4px 12px',
                  background: '#FEF3C7',
                  color: '#92400E',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Status: {filters.academicStatus}
                </span>
              )}
              {filters.academicYear && (
                <span style={{
                  padding: '4px 12px',
                  background: '#E0E7FF',
                  color: '#3730A3',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Academic Year: {filters.academicYear}
                </span>
              )}
              {filters.degree && (
                <span style={{
                  padding: '4px 12px',
                  background: '#F0FDF4',
                  color: '#166534',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Degree: {filters.degree}
                </span>
              )}
              {filters.year && (
                <span style={{
                  padding: '4px 12px',
                  background: '#FDF4FF',
                  color: '#7C2D12',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Year: {filters.year}
                </span>
              )}
              {filters.semester && (
                <span style={{
                  padding: '4px 12px',
                  background: '#FEFCE8',
                  color: '#A16207',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Semester: {filters.semester}
                </span>
              )}
              {filters.group && (
                <span style={{
                  padding: '4px 12px',
                  background: '#ECFDF5',
                  color: '#065F46',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Group: {filters.group}
                </span>
              )}
              {filters.module && (
                <span style={{
                  padding: '4px 12px',
                  background: '#EFF6FF',
                  color: '#1E40AF',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Module: {filters.module}
                </span>
              )}
              {filters.status && (
                <span style={{
                  padding: '4px 12px',
                  background: filters.status === 'Active' ? '#ECFDF5' : '#FEF2F2',
                  color: filters.status === 'Active' ? '#065F46' : '#DC2626',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Status: {filters.status}
                </span>
              )}
              {filters.studentWithId && (
                <span style={{
                  padding: '4px 12px',
                  background: '#FEF2F2',
                  color: '#DC2626',
                  borderRadius: 16,
                  fontSize: 12,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}>
                  Search: {filters.studentWithId}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Students Table */}
        <div style={{marginTop: 30}}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#F9FAFB',
              borderRadius: 8,
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#6B7280' }}>
                Loading students...
              </div>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#FEF2F2',
              borderRadius: 8,
              border: '1px solid #FECACA'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#DC2626' }}>
                {error}
              </div>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {students.length === 0 ? (
                <div style={{
                  textAlign: 'center', 
                  padding: '60px 20px',
                  background: '#F9FAFB'
                }}>
                  <div style={{
                    fontSize: 48,
                    marginBottom: 16
                  }}>👨‍🎓</div>
                  <div style={{
                    fontSize: 20,
                    fontWeight: '600',
                    marginBottom: 12,
                    color: '#374151',
                    fontFamily: 'Outfit'
                  }}>
                    No Students Found
                  </div>
                  <div style={{
                    color: '#6B7280',
                    marginBottom: 24,
                    lineHeight: 1.6,
                    fontFamily: 'Outfit'
                  }}>
                    {allStudents.length === 0 
                      ? "Your university currently has no students in the system."
                      : "No students match your current filter criteria."
                    }
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    {allStudents.length === 0 && (
                      <button 
                        onClick={() => navigate('/add-student')}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#F04915',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontFamily: 'Outfit',
                          fontWeight: '500'
                        }}
                      >
                        Add Students
                      </button>
                    )}
                    
                    <button 
                      onClick={resetFilters}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#6B7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Clear Filters
                    </button>
                    
                    <button 
                      onClick={fetchAllData}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Refresh Data
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Results Summary */}
                  <div style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #E5E7EB',
                    background: '#F9FAFB',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      fontSize: 16,
                      fontFamily: 'Outfit',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      Showing {students.length} of {allStudents.length} students
                      {Object.values(filters).some(value => value !== '') && (
                        <span style={{
                          marginLeft: 8,
                          fontSize: 14,
                          color: '#6B7280',
                          fontWeight: '400'
                        }}>
                          (filtered)
                        </span>
                      )}
                    </div>
                    
                    <button 
                      onClick={downloadCSV}
                      disabled={!students || students.length === 0}
                      style={{
                        padding: '8px 16px',
                        background: students && students.length > 0 ? '#10B981' : '#D1D5DB',
                        borderRadius: 6,
                        border: 'none',
                        color: 'white',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500',
                        cursor: students && students.length > 0 ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                      onMouseOver={(e) => {
                        if (students && students.length > 0) {
                          e.target.style.background = '#059669';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (students && students.length > 0) {
                          e.target.style.background = '#10B981';
                        }
                      }}
                      title={`Download ${students?.length || 0} students as CSV`}
                    >
                      📥 Export CSV
                    </button>
                  </div>

                  {/* Table */}
                  <div style={{ 
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                    borderRadius: '8px',
                    border: isMobile ? '1px solid #E5E7EB' : 'none'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead style={{ background: '#F9FAFB' }}>
                        <tr>
                          <th style={getTableHeaderStyle()}>No.</th>
                          <th style={getTableHeaderStyle()}>Student ID</th>
                          <th style={getTableHeaderStyle()}>Name</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Degree Programme</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Level</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Group</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Year/Semester</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Academic Status</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Status</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Modules</th>
                          <th style={{
                            padding: '16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #E5E7EB',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151'
                          }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student, index) => (
                          <tr key={student.id || index} style={{
                            borderBottom: '1px solid #F3F4F6'
                          }}>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {(index + 1).toString().padStart(2, '0')}.
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              fontWeight: '500',
                              color: '#111827'
                            }}>
                              {student.student_id || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              fontWeight: '500',
                              color: '#111827'
                            }}>
                              {student.name || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {student.degree_programme || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: '#EBF8FF',
                                color: '#1E40AF',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {/* Display degree_level from view data, but note it's not editable */}
                                {formatDisplayValue(student.degree_level) || 'N/A'}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {student.group_name || 'N/A'}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {`${student.current_year || 'N/A'}/${student.current_semester || 'N/A'}`}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: student.local_international === 'local' ? '#F0FDF4' : '#FEF3C7',
                                color: student.local_international === 'local' ? '#166534' : '#92400E',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {formatDisplayValue(student.local_international)}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              <span style={{
                                padding: '4px 8px',
                                background: student.active ? '#ECFDF5' : '#FEF2F2',
                                color: student.active ? '#065F46' : '#DC2626',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: '500'
                              }}>
                                {student.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151',
                              maxWidth: '250px',
                              minWidth: '200px'
                            }}>
                              {student.modules && student.modules.length > 0 ? (
                                <div style={{ 
                                  display: 'flex', 
                                  flexDirection: 'column', 
                                  gap: 4, 
                                  alignItems: 'center' 
                                }}>
                                  {student.modules.slice(0, 2).map((module, idx) => (
                                    <div key={idx} style={{ width: '100%' }}>
                                      <span 
                                        style={{
                                          display: 'inline-block',
                                          padding: '3px 8px',
                                          background: '#E0F2FE',
                                          color: '#0369A1',
                                          borderRadius: 12,
                                          fontSize: 11,
                                          fontWeight: '600',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          maxWidth: '180px',
                                          marginBottom: 2
                                        }}
                                        title={`${module.module_code} - ${module.module_name}`}
                                      >
                                        {module.module_code}
                                      </span>
                                      <div style={{
                                        fontSize: 9,
                                        color: '#6B7280',
                                        marginTop: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '180px'
                                      }}>
                                        {module.module_name}
                                      </div>
                                    </div>
                                  ))}
                                  {student.modules.length > 2 && (
                                    <div style={{
                                      fontSize: 10,
                                      color: '#6B7280',
                                      fontStyle: 'italic',
                                      marginTop: 4,
                                      cursor: 'pointer',
                                      textDecoration: 'underline'
                                    }}
                                    title={student.modules.slice(2).map(m => `${m.module_code} - ${m.module_name}`).join('\n')}
                                    >
                                      +{student.modules.length - 2} more modules
                                    </div>
                                  )}
                                  <div style={{
                                    fontSize: 10,
                                    color: '#10B981',
                                    fontWeight: '600',
                                    marginTop: 2
                                  }}>
                                    Total: {student.modules.length}
                                  </div>
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center' }}>
                                  <span style={{
                                    color: '#6B7280',
                                    fontSize: 12,
                                    fontStyle: 'italic'
                                  }}>
                                    No modules
                                  </span>
                                  <div style={{
                                    fontSize: 10,
                                    color: '#DC2626',
                                    marginTop: 2
                                  }}>
                                    Not enrolled
                                  </div>
                                </div>
                              )}
                            </td>
                            <td style={{
                              padding: '16px',
                              textAlign: 'center'
                            }}>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                <button 
                                  onClick={() => handleEditStudent(student.id)}
                                  style={{
                                    background: '#F59E0B',
                                    border: 'none',
                                    borderRadius: 6,
                                    color: 'white',
                                    padding: '8px 12px',
                                    fontSize: 12,
                                    fontFamily: 'Outfit',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseOver={(e) => e.target.style.background = '#D97706'}
                                  onMouseOut={(e) => e.target.style.background = '#F59E0B'}
                                >
                                  ✏️ Edit
                                </button>
                                
                                {/* Remove the delete button completely */}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentsList;